import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { enquiryAPI, degreeAPI } from '../../services/api'
import toast from 'react-hot-toast'
import LoadingSpinner from '../Common/LoadingSpinner'

const EnquiryModal = ({ isOpen, onClose, enquiryType = 'consultation', courseName, universityName, courseId, universityId }) => {
    const [isSubmitted, setIsSubmitted] = useState(false)

    // Fetch degrees from API
    const { data: degrees, isLoading: degreesLoading } = useQuery({
        queryKey: ['degrees'],
        queryFn: degreeAPI.getDegrees,
        enabled: isOpen, // Only fetch when modal is open
    })

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm()

    const enquiryMutation = useMutation({
        mutationFn: enquiryAPI.createEnquiry,
        onSuccess: (data) => {
            const successMessage = enquiryType === 'brochure'
                ? 'Brochure request submitted successfully! We will send you the detailed information soon.'
                : enquiryType === 'counselling'
                    ? 'Counselling request submitted successfully! Our experts will contact you soon.'
                    : 'Enquiry submitted successfully! We will contact you soon.'
            toast.success(successMessage)
            reset()
            setIsSubmitted(true)
            setTimeout(() => {
                setIsSubmitted(false)
                onClose()
            }, 2000)
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to submit enquiry')
        },
    })

    const onSubmit = (data) => {
        const enquiryData = {
            ...data,
            // Only include courseId if it's a valid course ID
            ...(courseId && { courseId }),
            // Only include universityId if it's provided
            ...(universityId ? { universityId } : {}),
            source: enquiryType === 'consultation' ? 'consultation' :
                enquiryType === 'brochure' ? 'brochure' :
                    enquiryType === 'counselling' ? 'counselling' : 'enquiry',
            enquiry_type: enquiryType,
            consent_terms: 'true',
        }

        enquiryMutation.mutate(enquiryData)
    }

    const handleClose = () => {
        if (!enquiryMutation.isPending) {
            reset()
            setIsSubmitted(false)
            onClose()
        }
    }

    if (!isOpen) return null

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                    {/* Background overlay */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
                        onClick={handleClose}
                    />

                    {/* Modal panel */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ duration: 0.2 }}
                        className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl relative z-10 max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="text-2xl font-bold text-gray-900">
                                    {enquiryType === 'consultation' ? 'Book Free Consultation' :
                                        enquiryType === 'brochure' ? 'Download Brochure' :
                                            enquiryType === 'counselling' ? 'Free Career Counselling' :
                                                'Quick Enquiry'}
                                </h3>
                                <p className="text-gray-600 mt-1">
                                    {enquiryType === 'consultation'
                                        ? 'Tell us about your requirements and we will get back to you'
                                        : enquiryType === 'brochure'
                                            ? 'Get detailed information about courses and programs'
                                            : enquiryType === 'counselling'
                                                ? 'Get personalized guidance for your career path'
                                                : 'Get instant information about courses and admissions'
                                    }
                                </p>
                            </div>
                            <button
                                onClick={handleClose}
                                disabled={enquiryMutation.isPending}
                                className="p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200 relative z-20 disabled:opacity-50"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        {isSubmitted ? (
                            <div className="py-8 text-center">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <CheckCircleIcon className="w-8 h-8 text-green-600" />
                                </div>
                                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                    Enquiry Submitted!
                                </h4>
                                <p className="text-gray-600">
                                    Thank you for your interest. We will get back to you within 24 hours.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                                {/* Degree Selection - Only show for consultation */}
                                {enquiryType === 'consultation' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Interested Degree Program *
                                        </label>
                                        {degreesLoading ? (
                                            <div className="flex justify-center py-4">
                                                <LoadingSpinner />
                                            </div>
                                        ) : (
                                            <select
                                                {...register('degreeId', { required: 'Please select a degree program' })}
                                                className="input"
                                            >
                                                <option value="">Select a degree program</option>
                                                {degrees?.data?.degrees?.map((degree) => (
                                                    <option key={degree._id} value={degree._id}>
                                                        {degree.name} ({degree.shortName}) - {degree.level}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                        {errors.degreeId && (
                                            <p className="mt-1 text-sm text-red-600">{errors.degreeId.message}</p>
                                        )}
                                    </div>
                                )}

                                {/* Personal Information */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        {...register('name', { required: 'Name is required' })}
                                        className="input"
                                        placeholder="Enter your full name"
                                    />
                                    {errors.name && (
                                        <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Email *
                                        </label>
                                        <input
                                            type="email"
                                            {...register('email', {
                                                required: 'Email is required',
                                                pattern: {
                                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                    message: 'Invalid email address'
                                                }
                                            })}
                                            className="input"
                                            placeholder="Enter your email"
                                        />
                                        {errors.email && (
                                            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Phone Number *
                                        </label>
                                        <input
                                            type="tel"
                                            {...register('phone', {
                                                required: 'Phone number is required',
                                                pattern: {
                                                    value: /^[\+]?[1-9][\d]{0,15}$/,
                                                    message: 'Invalid phone number'
                                                }
                                            })}
                                            className="input"
                                            placeholder="Enter your phone number"
                                        />
                                        {errors.phone && (
                                            <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            City *
                                        </label>
                                        <input
                                            type="text"
                                            {...register('city', { required: 'City is required' })}
                                            className="input"
                                            placeholder="Enter your city"
                                        />
                                        {errors.city && (
                                            <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            State
                                        </label>
                                        <input
                                            type="text"
                                            {...register('state')}
                                            className="input"
                                            placeholder="Enter your state"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Message (Optional)
                                    </label>
                                    <textarea
                                        {...register('message')}
                                        rows={4}
                                        className="input"
                                        placeholder="Tell us about your requirements or questions..."
                                    />
                                </div>

                                <div className="flex items-start space-x-3">
                                    <input
                                        type="checkbox"
                                        id="consent_marketing"
                                        {...register('consent_marketing')}
                                        className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="consent_marketing" className="text-sm text-gray-600">
                                        I agree to receive updates about courses and educational opportunities via email and WhatsApp
                                    </label>
                                </div>

                                <div className="flex items-start space-x-3">
                                    <input
                                        type="checkbox"
                                        id="consent_terms"
                                        {...register('consent_terms', { required: 'You must agree to the terms' })}
                                        className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="consent_terms" className="text-sm text-gray-600">
                                        I agree to the <a href="/terms" className="text-primary-600 hover:underline">Terms of Service</a> and <a href="/privacy" className="text-primary-600 hover:underline">Privacy Policy</a> *
                                    </label>
                                </div>
                                {errors.consent_terms && (
                                    <p className="text-sm text-red-600">{errors.consent_terms.message}</p>
                                )}

                                <div className="flex justify-end space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        disabled={enquiryMutation.isPending}
                                        className="px-6 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={enquiryMutation.isPending}
                                        className="btn-primary btn-lg"
                                    >
                                        {enquiryMutation.isPending ? (
                                            <div className="flex items-center space-x-2">
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                <span>Submitting...</span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center space-x-2">
                                                <CheckCircleIcon className="w-5 h-5" />
                                                <span>Submit Enquiry</span>
                                            </div>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </motion.div>
                </div>
            </div>
        </AnimatePresence>
    )
}

export default EnquiryModal
